/**
 * 등록 안 된 게임 주소(2026-10-01 `unige.ai.kr`)의 Google·네이버 로그인 — 같은 Worker 의 **등록된 주소** 새 창에서 공급자 로그인을
 * 끝내고, 이 창은 **일회용 코드**만 받아 자기 서버에서 계정 열쇠로 바꾼다 (DESIGN 14.448). 서버 쪽은 `src/api/auth-relay.ts`.
 *
 * - 받는 메시지는 셋을 다 맞춘 것만: **자기 서버가 알려준 중계 출처(정확한 문자열)** · **이 창이 연 그 창** · **이 창이 만든 nonce**.
 * - 코드는 이 창이 만든 검증값(verifier)과 함께 내야 바뀐다. 검증값은 이 탭 밖으로 나가지 않는다(창에는 해시만 건넨다).
 * - 팝업이 막히면 같은 창으로 다녀온다 — 돌아오는 주소의 `#` 조각에 코드가 실린다(서버 접속 기록에 안 남는다). 읽자마자 지운다.
 */
const PENDING = 'unige.auth.relay';
/** index.html 머리 스크립트가 같은 창 복귀의 조각을 옮겨 두는 자리(DESIGN 14.448). */
const RETURN = 'unige.auth.relay.return';
const HEX64 = /^[a-f0-9]{64}$/;
const TIMEOUT = 10 * 60_000;
let active = null;

/** 사용자가 로그인 창을 닫았다 — 실패가 아니라 취소다(붉은 글씨를 쓰지 않는다). */
export class RelayCancelled extends Error {
  constructor() { super('로그인 창을 닫았다. 현재 계정은 그대로다.'); this.name = 'RelayCancelled'; }
}

const randomHex = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, '0')).join('');
async function sha256Hex(text) {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');
}
function save(pending) {
  try { sessionStorage.setItem(PENDING, JSON.stringify(pending)); return true; } catch { return false; }
}
function load() {
  try { return JSON.parse(sessionStorage.getItem(PENDING) || 'null'); } catch { return null; }
}
export function clearRelayPending() {
  try { sessionStorage.removeItem(PENDING); } catch { /* 저장 불가 환경 — 남은 것이 없다 */ }
}

/**
 * **누름 처리기 안에서 첫 await 전에** 부른다 — 팝업 허용은 사용자의 누름에 묶여 있다. 빈 창부터 열고, 주소는 검증값 해시를
 * 만든 뒤 `runAuthRelay` 가 넣는다. 막히면 null(같은 창으로 다녀온다).
 */
export function openRelayWindow() {
  try { return window.open('', 'unige-auth-relay', 'popup,width=480,height=700') || null; } catch { return null; }
}

/**
 * 등록된 주소 창에서 로그인을 끝내고 `{ code, verifier }` 를 돌려준다. 팝업이 막혀 같은 창으로 떠나면 이 약속은 끝나지 않는다
 * (페이지가 바뀌고, 돌아오면 `takeRelayReturn` 이 이어 받는다).
 * @param {Window|null} popup openRelayWindow() 결과
 * @param {{provider:'google'|'naver', relay:string, purpose?:'login'|'unlink', allowGuest?:boolean, fingerprint:string}} opts
 */
export async function runAuthRelay(popup, { provider, relay, purpose = 'login', allowGuest = false, fingerprint }) {
  let target;
  try { target = new URL(relay); } catch { target = null; }
  if (!target || target.protocol !== 'https:' || target.origin !== relay) {
    try { popup?.close(); } catch { /* 이미 닫힘 */ }
    throw new Error('로그인 주소를 확인하지 못했다. 잠시 뒤 다시 시도해 달라.');
  }
  active?.cancel();
  const pending = { provider, purpose, allowGuest: !!allowGuest, relay, verifier: randomHex(), nonce: randomHex(), fingerprint, at: Date.now() };
  const params = new URLSearchParams({ p: provider, o: location.origin, c: await sha256Hex(pending.verifier), n: pending.nonce });
  if (purpose === 'unlink') params.set('a', 'unlink');
  if (allowGuest) params.set('g', '1');
  if (!popup || popup.closed) {
    params.set('m', 'redirect');
    if (!save({ ...pending, mode: 'redirect' })) throw new Error('이 브라우저에 로그인 확인 정보를 잠시 저장하지 못했다. 팝업을 허용한 뒤 다시 눌러 달라.');
    location.assign(`${relay}/auth-relay.html#${params}`);
    return new Promise(() => {});
  }
  params.set('m', 'popup');
  try { popup.location.replace(`${relay}/auth-relay.html#${params}`); } catch { throw new Error('로그인 창을 열지 못했다. 다시 눌러 달라.'); }
  return new Promise((resolve, reject) => {
    let done = false;
    const finish = (fn, value) => {
      if (done) return;
      done = true;
      if (active === attempt) active = null;
      removeEventListener('message', receive); clearInterval(poll); clearTimeout(timer);
      fn(value);
    };
    const receive = (event) => {
      if (event.origin !== relay || event.source !== popup) return;
      const d = event.data;
      if (!d || typeof d !== 'object' || d.nonce !== pending.nonce || d.provider !== provider) return;
      if (d.type === 'unige:auth-relay' && HEX64.test(String(d.code))) {
        try { popup.postMessage({ type: 'unige:auth-relay-ack', nonce: pending.nonce }, relay); } catch { /* 창이 먼저 닫혔다 */ }
        finish(resolve, { code: d.code, verifier: pending.verifier });
      } else if (d.type === 'unige:auth-relay-error') {
        finish(reject, new Error(String(d.message || '').slice(0, 200) || '로그인을 마치지 못했다. 다시 시도해 달라.'));
      }
    };
    const attempt = { cancel: () => finish(reject, new RelayCancelled()) };
    active = attempt;
    addEventListener('message', receive);
    const poll = setInterval(() => { if (popup.closed) finish(reject, new RelayCancelled()); }, 500);
    const timer = setTimeout(() => finish(reject, new Error('로그인 창이 오래 응답하지 않는다. 다시 시도해 달라.')), TIMEOUT);
  });
}

/**
 * 같은 창으로 다녀온 경우(팝업 차단) — `?auth=<provider>#relay=<코드>&n=<nonce>` 를 읽고 **곧바로 조각을 지운다**.
 * 이 탭이 시작한 것(공급자·nonce·방식)과 맞을 때만 코드를 내준다. 남이 만든 주소로 들어오면 이 탭에는 검증값이 없어 거절된다.
 * @returns {null | {error:string} | {code:string, verifier:string, purpose:string, fingerprint:string}}
 */
export function takeRelayReturn(provider) {
  const url = new URL(location.href);
  if (url.searchParams.get('auth') !== provider) return null;
  // index.html 머리의 첫 스크립트가 조각을 이미 걷어 이 탭에 옮겨 두었다(광고·분석 스크립트가 주소를 읽기 전에).
  let early = null;
  try {
    early = JSON.parse(sessionStorage.getItem(RETURN) || 'null');
    sessionStorage.removeItem(RETURN);
  } catch { early = null; }
  const hash = new URLSearchParams(early?.p === provider ? String(early.h) : url.hash.slice(1));
  const code = hash.get('relay'), failed = hash.get('relay-error'), nonce = hash.get('n');
  if (code === null && failed === null) return null;
  if (url.hash) history.replaceState(history.state, '', url.pathname + url.search);
  const pending = load();
  clearRelayPending();
  if (!pending || pending.provider !== provider || pending.mode !== 'redirect' || pending.nonce !== nonce) {
    return { error: '로그인을 시작한 창과 돌아온 창이 다르다. 다시 로그인해 달라.' };
  }
  if (Date.now() - Number(pending.at) > TIMEOUT) return { error: '로그인 확인 시간이 지났다. 다시 로그인해 달라.' };
  if (failed !== null) return { error: '로그인이 취소되었거나 확인되지 않았다. 다시 시도해 달라.' };
  if (!HEX64.test(String(code))) return { error: '로그인 결과를 확인하지 못했다. 다시 로그인해 달라.' };
  return { code, verifier: pending.verifier, purpose: pending.purpose, fingerprint: pending.fingerprint };
}
